import React, { useMemo } from 'react';

import Typography from '../../library/typography/typography';
import DollarCircleSolidIcon from '../../shared/icons/circle-dollar-solid-icon';
import {
  MedalSolidIcon,
  PennantSolidIcon,
  TagsSolidIcon
} from '../../shared/icons/icons';
import Flag from '../flag/flag';
import TreadwellRankBanner from '../treadwell-rank-banner/treadwell-rank-banner';
import WebOnlyFlag from '../web-only-flag/web-only-flag';

import {
  getPrioritizedArticleStatusProduct,
  isWebTire
} from '../../shared/utils/product/product';

import { BundleData, ProductData } from '../../shared/types/graphql-types';
import { PROMOTION_CONDITION_TYPES } from '../../shared/constants/cart';
import { ARTICLE_STATUSES } from '../../shared/constants/products';

import './product-flags.scss';

interface FlaggedProduct {
  hasPromo?: boolean;
  hasRebate?: boolean;
  isExclusive?: boolean;
  isFeatured?: boolean;
}

interface ProductFlagsProps {
  className?: string;
  hideFeaturedAndExclusive?: boolean;
  hideLabel?: boolean;
  hideTreadwellRank?: boolean;
  hideWebOnly?: boolean;
  prebuiltPackage?: BundleData;
  product?: ProductData;
}

function ProductFlags(props: ProductFlagsProps) {
  const {
    className = '',
    hideFeaturedAndExclusive = false,
    hideLabel = false,
    hideTreadwellRank = false,
    hideWebOnly = false,
    prebuiltPackage,
    product
  } = props;

  const { product1, product2 } = prebuiltPackage || {};
  const isDiscontinued = prebuiltPackage
    ? getPrioritizedArticleStatusProduct(product1, product2)?.articleStatus ===
      ARTICLE_STATUSES.DISCONTINUED
    : product?.articleStatus === ARTICLE_STATUSES.DISCONTINUED;

  const isBackordered = prebuiltPackage
    ? getPrioritizedArticleStatusProduct(product1, product2)?.articleStatus ===
      ARTICLE_STATUSES.BACKORDER
    : product?.articleStatus === ARTICLE_STATUSES.BACKORDER;

  const { rank } = product?.recommendationData || {};
  const { origEquipmentData } = product || {};
  const isOriginalEquipment = Boolean(origEquipmentData);
  const isWebOnly = isWebTire(product);

  const { hasPromo, hasRebate, isExclusive, isFeatured } = useMemo(
    () =>
      [product, product1, product2].reduce(
        (flaggedProduct: FlaggedProduct, currentProduct): FlaggedProduct => {
          const { isCoreProduct, isExclusive, potentialPromotions } =
            currentProduct || {};

          const hasPromo = potentialPromotions?.some(
            promotion =>
              promotion?.promotionConditionType ===
                PROMOTION_CONDITION_TYPES.TREADWELL ||
              promotion?.promotionConditionType ===
                PROMOTION_CONDITION_TYPES.MYACCOUNT
          );

          const hasRebate = potentialPromotions?.some(
            promotion =>
              promotion?.promotionConditionType ===
              PROMOTION_CONDITION_TYPES.OTHER
          );
          return {
            hasPromo: flaggedProduct.hasPromo || hasPromo,
            hasRebate: flaggedProduct.hasRebate || hasRebate,
            isExclusive: Boolean(flaggedProduct.isExclusive || isExclusive),
            isFeatured: Boolean(flaggedProduct.isFeatured || isCoreProduct)
          };
        },
        {
          hasPromo: false,
          hasRebate: false,
          isExclusive: false,
          isFeatured: false
        }
      ),
    [product, product1, product2]
  );

  const isFeaturedOrExclusive = isFeatured || isExclusive;

  const getFlags = () => {
    const flags: JSX.Element[] = [];

    if (!hideTreadwellRank && rank) {
      flags.push(
        <TreadwellRankBanner
          className="product-flags__treadwell-rank-banner"
          hideLabel={hideLabel}
          key="treadwell"
          rank={rank}
          styleName="tw-rank-banner"
        />
      );
    }

    if (!isDiscontinued && !isBackordered) {
      if (
        isFeaturedOrExclusive &&
        hideTreadwellRank &&
        !hideFeaturedAndExclusive
      ) {
        flags.push(
          <Flag
            key="featuredExclusive"
            renderIcon={
              isFeatured ? (
                <PennantSolidIcon styleName="flag-icon" />
              ) : (
                <MedalSolidIcon styleName="flag-icon" />
              )
            }
            styleName={`flag ${
              isFeatured ? 'featured-flag' : 'exclusive-flag'
            }`}
          >
            {!hideLabel ? `${isFeatured ? 'FEATURED' : 'EXCLUSIVE'}` : ''}
          </Flag>
        );
      }

      if (hasPromo) {
        flags.push(
          <Flag
            key="savings"
            renderIcon={<TagsSolidIcon styleName="flag-icon" />}
            styleName="flag savings-flag"
          >
            {!hideLabel ? 'SAVINGS' : ''}
          </Flag>
        );
      }

      if (
        hasRebate &&
        !hasPromo &&
        (flags.length < 2 || (flags.length === 2 && !isOriginalEquipment))
      ) {
        flags.push(
          <Flag
            key="rebate"
            renderIcon={<DollarCircleSolidIcon styleName="flag-icon" />}
            styleName="flag rebate-flag"
          >
            {!hideLabel ? 'REBATE AVAILABLE' : ''}
          </Flag>
        );
      }
    }

    if (
      isOriginalEquipment &&
      (flags.length < 3 || (flags.length === 2 && hasRebate))
    ) {
      flags.push(
        <Flag
          key="oe"
          renderIcon={
            <Typography
              size="small"
              styleName="flag-icon oe-flag-icon"
              tag="span"
              weight="black"
            >
              O.E.
            </Typography>
          }
          styleName="flag oe-flag"
        >
          {!hideLabel ? ' ORIGINAL EQUIPMENT' : ''}
        </Flag>
      );
    }

    if (!hideWebOnly && isWebOnly) {
      flags.push(
        <WebOnlyFlag
          hideLabel={hideLabel}
          key="web-only"
          styleName="flag web-only-flag"
        />
      );
    }

    return flags;
  };

  return (
    <span className={`product-flags-one-dt ${className}`} styleName="container">
      {getFlags()}
    </span>
  );
}

export default ProductFlags;
